import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { PRIVACY_URL, TERMS_URL, LICENCES_PATH } from '../lib/legal'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { CREW_TABS, tabFromPath, currentMembership, crewError } from './lib/crewHome'

// The Crew Package (/crew/*): the crew member's own space. It needs a signed-in user only - not the yacht config - so it keeps
// working after the yacht login is turned off or removed. First visit: crew_sync_me() makes the profile from the crew login
// and copies the yacht's records in (supabase/migrations/20261024_crew_package.sql). Mobile first: bottom tab bar.
export default function CrewApp() {
  const { user, loading, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const tab = tabFromPath(location.pathname)
  const [state, setState] = useState({ busy: true, err: '', profile: null, memberships: [] })

  useEffect(() => {
    if (!user) return
    let live = true
    ;(async () => {
      const sync = await supabase.rpc('crew_sync_me')
      if (sync.error) { if (live) setState({ busy: false, err: crewError(sync.error), profile: null, memberships: [] }); return }
      const [p, m] = await Promise.all([
        supabase.from('crew_profiles').select('id, full_name, nationality').maybeSingle(),
        supabase.from('crew_memberships').select('id, vessel_name, rank, start_date, end_date, verified').order('start_date', { ascending: false }),
      ])
      if (live) setState({ busy: false, err: p.error || m.error ? crewError(p.error || m.error) : '', profile: p.data, memberships: m.data || [] })
    })()
    return () => { live = false }
  }, [user?.id]) // eslint-disable-line

  if (loading) return <div style={S.center}><div className="spin" /></div>
  if (!user) return <Navigate to="/login" replace />

  const now = currentMembership(state.memberships)
  return (
    <div style={S.page}>
      <header style={S.head}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{state.profile?.full_name || 'Crew'}</div>
        <button className="btn" style={S.link} onClick={signOut}>Log out</button>
      </header>
      <main style={S.main}>
        {state.busy ? <div style={S.muted}>Loading{'…'}</div>
          : state.err ? <div style={S.card}>{state.err}</div>
          : tab === 'home' ? (
            <div style={S.card}>
              <div style={S.label}>Current yacht</div>
              <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{now ? now.vessel_name || 'Unnamed vessel' : 'Not on a yacht'}</div>
              {now && <div style={S.muted}>{[now.rank, 'since ' + now.start_date].filter(Boolean).join(' · ')}</div>}
              <div style={{ ...S.muted, marginTop: 10 }}>{state.memberships.length} {state.memberships.length === 1 ? 'yacht' : 'yachts'} in your record</div>
            </div>
          ) : (
            <div style={S.card}><div style={S.muted}>{CREW_TABS.find(t => t.id === tab).label}: coming next.</div></div>
          )}
        <div style={{ ...S.muted, textAlign: 'center', fontSize: 12 }}>
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)' }}>Privacy</a>{' \u00b7 '}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)' }}>Terms</a>{' \u00b7 '}
          <a href={LICENCES_PATH} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)' }}>Open-source licences</a>
        </div>
      </main>
      <nav style={S.tabs}>
        {CREW_TABS.map(t => (
          <button key={t.id} onClick={() => navigate(t.id === 'home' ? '/crew' : '/crew/' + t.id)}
            style={{ ...S.tab, color: t.id === tab ? 'var(--gold)' : 'var(--muted)', fontWeight: t.id === tab ? 700 : 500 }}>{t.label}</button>
        ))}
      </nav>
    </div>
  )
}

const S = {
  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--ink)', color: 'var(--text)' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '0.5px solid var(--border)' },
  main: { flex: 1, padding: 16, paddingBottom: 80, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 560, width: '100%', margin: '0 auto', boxSizing: 'border-box' },
  card: { background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 12, padding: 16 },
  label: { fontSize: 12, color: 'var(--muted)' },
  muted: { fontSize: 13, color: 'var(--muted)', marginTop: 4 },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' },
  link: { background: 'none', border: 'none', color: 'var(--muted)', fontSize: 13, cursor: 'pointer' },
  tabs: { position: 'fixed', bottom: 0, left: 0, right: 0, display: 'flex', background: 'var(--hull)', borderTop: '0.5px solid var(--border)', paddingBottom: 'env(safe-area-inset-bottom)' },
  tab: { flex: 1, padding: '12px 4px', background: 'none', border: 'none', fontSize: 12, cursor: 'pointer' },
}
